import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

// Configurable tax rate
const TAX_RATE = 0.135; // 13.5%
// Configurable service fee
const SERVICE_FEE = 0; // $0

const OrderSummary = ({ 
  dish, 
  quantity, 
  deliveryOption,
  onPlaceOrder,
  isProcessing,
  subtotal: subtotalProp,
  deliveryFee: deliveryFeeProp,
  serviceFee: serviceFeeProp,
  tax: taxProp,
  total: totalProp,
}) => {
  // Prefer the supplier's stored price per plate, fallback to legacy `price`
  const dishPrice = Number(dish?.price_per_plate ?? dish?.price ?? 0);

  // Use totals passed from parent when available (cart checkout), otherwise compute locally
  const subtotal = subtotalProp !== undefined ? subtotalProp : dishPrice * quantity;
  const deliveryFee = deliveryFeeProp !== undefined ? deliveryFeeProp : (deliveryOption === 'delivery' ? 3.99 : 0);
  const serviceFee = serviceFeeProp !== undefined ? serviceFeeProp : SERVICE_FEE;
  const tax = taxProp !== undefined ? taxProp : (subtotal + deliveryFee + serviceFee) * TAX_RATE;
  const total = totalProp !== undefined ? totalProp : subtotal + deliveryFee + serviceFee + tax;

  const summaryItems = [
    {
      label: 'Subtotal',
      value: subtotal,
      icon: 'Receipt'
    },
    {
      label: deliveryOption === 'delivery' ? 'Delivery Fee' : 'Pickup',
      value: deliveryFee,
      icon: deliveryOption === 'delivery' ? 'Truck' : 'Store'
    },
    {
      label: 'Service Fee',
      value: serviceFee,
      icon: 'DollarSign'
    },
    {
      label: `Tax (${(TAX_RATE * 100)?.toFixed(1)}%)`,
      value: tax,
      icon: 'FileText'
    }
  ];

  return (
    <div className="bg-card rounded-xl shadow-warm p-4 md:p-6 sticky top-24">
      <div className="flex items-center gap-3 mb-4 md:mb-6">
        <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon name="ShoppingCart" size={20} color="var(--color-primary)" />
        </div>
        <div>
          <h3 className="text-lg md:text-xl font-heading font-bold text-foreground">
            Order Summary
          </h3>
          <p className="text-xs md:text-sm text-muted-foreground">
            Review your order details
          </p>
        </div>
      </div>
      <div className="space-y-4 mb-6">
        <div className="flex items-center justify-between pb-4 border-b border-border">
          <div className="flex-1 min-w-0">
            <div className="text-sm md:text-base font-medium text-foreground line-clamp-1 mb-1">
              {dish?.name}
            </div>
            <div className="text-xs md:text-sm text-muted-foreground">
              Quantity: <span className="font-medium data-text">{quantity}</span> × ${dishPrice?.toFixed(2)}
            </div>
          </div>
        </div>

        {summaryItems?.map((item, index) => (
          <div key={index} className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon name={item?.icon} size={16} color="var(--color-muted-foreground)" />
              <span className="text-sm md:text-base text-muted-foreground">
                {item?.label}
              </span>
            </div>
            <span className="text-sm md:text-base font-medium data-text">
              ${item?.value?.toFixed(2)}
            </span>
          </div>
        ))}

        <div className="flex items-center justify-between pt-4 border-t-2 border-border">
          <span className="text-base md:text-lg font-heading font-bold text-foreground">
            Total Amount
          </span>
          <span className="text-xl md:text-2xl font-heading font-bold text-primary data-text">
            ${total?.toFixed(2)}
          </span>
        </div>
      </div>
      <div className="space-y-3">
        <Button
          variant="default"
          fullWidth
          size="lg"
          onClick={onPlaceOrder}
          loading={isProcessing}
          iconName="Wallet"
          iconPosition="left"
        >
          Cash on Delivery
        </Button>

        <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
          <Icon name="Shield" size={16} color="var(--color-success)" />
          <span>Secure checkout with 256-bit SSL encryption</span>
        </div>

        <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
          <Icon name="RotateCcw" size={16} color="var(--color-primary)" />
          <span>Free cancellation up to 2 hours before delivery</span>
        </div>
      </div>
      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex items-start gap-3 bg-primary/5 rounded-lg p-3">
          <Icon name="Info" size={18} color="var(--color-primary)" />
          <div className="flex-1 text-xs md:text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Order Guarantee:</span> If your order doesn't meet expectations, we'll make it right with a full refund or replacement.
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;